/**
 * Service for managing hierarchical presupuesto structure
 * Handles CRUD operations for PresupuestoVenta/Costo Areas, Partidas, and Recursos
 */

import api from './api';

// Derivar URL base para OneBase desde la baseURL actual de `api`
// Si api.baseURL es '/api/content', entonces API_URL debe ser '/api/onebase'
const API_URL = (() => {
  const baseURL = api?.defaults?.baseURL || '/api/content';
  // Remover '/content' y agregar '/onebase'
  return baseURL.replace(/\/content\/?$/i, '/onebase');
})();

const presupuestoHierarchyService = {
  /**
   * PresupuestoVentaArea Operations
   */

  // Create a new area directly in presupuesto (not from catalog)
  createVentaArea: (data) => {
    return api.post(`${API_URL}/presupuestos-venta-areas/`, data);
  },

  // Delete an area from presupuesto venta
  deleteVentaArea: (id) => {
    return api.delete(`${API_URL}/presupuestos-venta-areas/${id}/`);
  },

  /**
   * PresupuestoCostoArea Operations
   */

  // Create a new area directly in presupuesto costo
  createCostoArea: (data) => {
    return api.post(`${API_URL}/presupuestos-costo-areas/`, data);
  },

  // Delete an area from presupuesto costo
  deleteCostoArea: (id) => {
    return api.delete(`${API_URL}/presupuestos-costo-areas/${id}/`);
  },

  /**
   * PresupuestoVentaPartida Operations
   */

  // Get all partidas for a specific area
  getVentaPartidas: (areaId) => {
    return api.get(`${API_URL}/presupuestos-venta-partidas/`, {
      params: { area: areaId }
    });
  },

  // Get a specific partida
  getVentaPartida: (id) => {
    return api.get(`${API_URL}/presupuestos-venta-partidas/${id}/`);
  },

  // Create a new partida
  createVentaPartida: (data) => {
    return api.post(`${API_URL}/presupuestos-venta-partidas/`, data);
  },

  // Update an existing partida
  updateVentaPartida: (id, data) => {
    return api.patch(`${API_URL}/presupuestos-venta-partidas/${id}/`, data);
  },

  // Delete a partida
  deleteVentaPartida: (id) => {
    return api.delete(`${API_URL}/presupuestos-venta-partidas/${id}/`);
  },

  /**
   * PresupuestoVentaRecurso Operations
   */

  // Get all recursos for a specific partida
  getVentaRecursos: (partidaId) => {
    return api.get(`${API_URL}/presupuestos-venta-recursos/`, {
      params: { partida: partidaId }
    });
  },

  // Get a specific recurso
  getVentaRecurso: (id) => {
    return api.get(`${API_URL}/presupuestos-venta-recursos/${id}/`);
  },

  // Create a new recurso
  createVentaRecurso: (data) => {
    return api.post(`${API_URL}/presupuestos-venta-recursos/`, data);
  },

  // Update an existing recurso
  updateVentaRecurso: (id, data) => {
    return api.patch(`${API_URL}/presupuestos-venta-recursos/${id}/`, data);
  },

  // Delete a recurso
  deleteVentaRecurso: (id) => {
    return api.delete(`${API_URL}/presupuestos-venta-recursos/${id}/`);
  },

  /**
   * PresupuestoCostoPartida Operations
   */

  // Get all partidas for a specific area
  getCostoPartidas: (areaId) => {
    return api.get(`${API_URL}/presupuestos-costo-partidas/`, {
      params: { area: areaId }
    });
  },

  // Get a specific partida
  getCostoPartida: (id) => {
    return api.get(`${API_URL}/presupuestos-costo-partidas/${id}/`);
  },

  // Create a new partida
  createCostoPartida: (data) => {
    return api.post(`${API_URL}/presupuestos-costo-partidas/`, data);
  },

  // Update an existing partida
  updateCostoPartida: (id, data) => {
    return api.patch(`${API_URL}/presupuestos-costo-partidas/${id}/`, data);
  },

  // Delete a partida
  deleteCostoPartida: (id) => {
    return api.delete(`${API_URL}/presupuestos-costo-partidas/${id}/`);
  },

  /**
   * PresupuestoCostoRecurso Operations
   */

  // Get all recursos for a specific partida
  getCostoRecursos: (partidaId) => {
    return api.get(`${API_URL}/presupuestos-costo-recursos/`, {
      params: { partida: partidaId }
    });
  },

  // Get a specific recurso
  getCostoRecurso: (id) => {
    return api.get(`${API_URL}/presupuestos-costo-recursos/${id}/`);
  },

  // Create a new recurso
  createCostoRecurso: (data) => {
    return api.post(`${API_URL}/presupuestos-costo-recursos/`, data);
  },

  // Update an existing recurso
  updateCostoRecurso: (id, data) => {
    return api.patch(`${API_URL}/presupuestos-costo-recursos/${id}/`, data);
  },

  // Delete a recurso
  deleteCostoRecurso: (id) => {
    return api.delete(`${API_URL}/presupuestos-costo-recursos/${id}/`);
  },

  /**
   * Batch operations for assigning from catalog
   */

  // Assign multiple areas from catalog to presupuesto venta
  assignCatalogAreasToVenta: (presupuestoId, areas) => {
    console.log('🔍 assignCatalogAreasToVenta() called with:');
    console.log('   presupuestoId:', presupuestoId);
    console.log('   areas:', areas);

    const requests = areas.map(area => {
      const payload = {
        presupuesto_venta: presupuestoId,
        area_referencia: area.id,  // Reference to master catalog area
        nombre: area.nombre,        // Copy the name from master
        descripcion: area.descripcion || ''  // Copy the description
      };
      console.log('📤 Payload for venta area:', payload);
      return api.post(`${API_URL}/presupuestos-venta-areas/`, payload)
        .then(response => {
          console.log('✅ Area created successfully:', response.data);
          return response;
        })
        .catch(error => {
          console.error('❌ Error creating area:', error.response?.data || error.message);
          throw error;
        });
    });
    return Promise.all(requests);
  },

  // Assign multiple areas from catalog to presupuesto costo
  assignCatalogAreasToCosto: (presupuestoId, areas) => {
    console.log('🔍 assignCatalogAreasToCosto() called with:');
    console.log('   presupuestoId:', presupuestoId);
    console.log('   areas:', areas);

    const requests = areas.map(area => {
      const payload = {
        presupuesto_costo: presupuestoId,
        area_referencia: area.id,  // Reference to master catalog area
        nombre: area.nombre,        // Copy the name from master
        descripcion: area.descripcion || ''  // Copy the description
      };
      console.log('📤 Payload for costo area:', payload);
      return api.post(`${API_URL}/presupuestos-costo-areas/`, payload)
        .then(response => {
          console.log('✅ Area created successfully:', response.data);
          return response;
        })
        .catch(error => {
          console.error('❌ Error creating area:', error.response?.data || error.message);
          throw error;
        });
    });
    return Promise.all(requests);
  },

  /**
   * Get full hierarchy for a presupuesto area (with nested partidas and recursos)
   */
  getVentaAreaHierarchy: (areaId) => {
    return api.get(`${API_URL}/presupuestos-venta-areas/${areaId}/`);
  },

  getCostoAreaHierarchy: (areaId) => {
    return api.get(`${API_URL}/presupuestos-costo-areas/${areaId}/`);
  },

  /**
   * Get areas assigned to a presupuesto
   */
  getVentaAreasForPresupuesto: (presupuestoVentaId) => {
    return api.get(`${API_URL}/presupuestos-venta-areas/`, {
      params: { presupuesto_venta: presupuestoVentaId }
    });
  },

  getCostoAreasForPresupuesto: (presupuestoCostoId) => {
    return api.get(`${API_URL}/presupuestos-costo-areas/`, {
      params: { presupuesto_costo: presupuestoCostoId }
    });
  },

  /**
   * Get presupuesto costo details
   */
  getPresupuestoCosto: (presupuestoCostoId) => {
    return api.get(`${API_URL}/presupuestos-costo/${presupuestoCostoId}/`);
  },

  /**
   * Get proyecto details
   */
  getProyecto: (proyectoId) => {
    return api.get(`${API_URL}/proyectos/${proyectoId}/`);
  },

  /**
   * Catálogo Maestro - Áreas
   */

  // Get all areas from master catalog
  getMaestroAreas: () => {
    return api.get(`${API_URL}/areas/`);
  },

  // Get a specific area from master catalog with its partidas and recursos
  getMaestroArea: (id) => {
    return api.get(`${API_URL}/areas/${id}/`);
  },

  // Create a new area in the master catalog
  createMaestroArea: (data) => {
    return api.post(`${API_URL}/areas/`, data);
  },

  /**
   * Catálogo Maestro - Partidas
   */

  // Get all partidas from master catalog
  getMaestroPartidas: (areaId = null) => {
    const params = areaId ? { area: areaId } : {};
    return api.get(`${API_URL}/partidas/`, { params });
  },

  // Get a specific partida from master catalog
  getMaestroPartida: (id) => {
    return api.get(`${API_URL}/partidas/${id}/`);
  },

  // Create a new partida in the master catalog
  createMaestroPartida: (data) => {
    return api.post(`${API_URL}/partidas/`, data);
  },

  // Update a partida in the master catalog
  updateMaestroPartida: (id, data) => {
    return api.patch(`${API_URL}/partidas/${id}/`, data);
  },

  // Delete a partida from the master catalog
  deleteMaestroPartida: (id) => {
    return api.delete(`${API_URL}/partidas/${id}/`);
  },

  /**
   * Catálogo Maestro - Recursos
   */

  // Get all recursos from master catalog
  getMaestroRecursos: (partidaId = null) => {
    const params = partidaId ? { partida: partidaId } : {};
    return api.get(`${API_URL}/recursos/`, { params });
  },

  // Get a specific recurso from master catalog
  getMaestroRecurso: (id) => {
    return api.get(`${API_URL}/recursos/${id}/`);
  },

  // Create a new recurso in the master catalog
  createMaestroRecurso: (data) => {
    return api.post(`${API_URL}/recursos/`, data);
  },

  // Update a recurso in the master catalog
  updateMaestroRecurso: (id, data) => {
    return api.patch(`${API_URL}/recursos/${id}/`, data);
  },

  // Delete a recurso from the master catalog
  deleteMaestroRecurso: (id) => {
    return api.delete(`${API_URL}/recursos/${id}/`);
  }
};

export default presupuestoHierarchyService;
